import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api, unwrap, unwrapPage } from '@/api/client';
import type { components } from '@/api/schema';

export type CasoIa = components['schemas']['AiCase'];
export type ContenidoCaso = components['schemas']['AiCaseContent'];
export type PreguntaIa = components['schemas']['AiQuestion'];
export type Trabajo = components['schemas']['AiJob'];
export type Proveedor = components['schemas']['AiProvider'];
export type TareaIa = components['schemas']['AiTaskSetting'];
export type EstadisticasIa = components['schemas']['AiStats'];
export type EstadoCaso = CasoIa['status'];

export function useCasosIa(query: { page: number; status?: EstadoCaso; specialtyId?: number }) {
  return useQuery({
    queryKey: ['ia', 'casos', query],
    queryFn: () =>
      unwrapPage<CasoIa[]>(
        api.GET('/api/v1/admin/ai/cases', { params: { query: { ...query, pageSize: 20 } } }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useCasoIa(id: number | null) {
  return useQuery({
    queryKey: ['ia', 'caso', id],
    queryFn: () =>
      unwrap<CasoIa>(
        api.GET('/api/v1/admin/ai/cases/{id}', { params: { path: { id: String(id) } } }),
      ),
    enabled: id !== null,
  });
}

export function useTrabajos(activo: boolean) {
  return useQuery({
    queryKey: ['ia', 'trabajos'],
    queryFn: () => unwrap<Trabajo[]>(api.GET('/api/v1/admin/ai/jobs')),
    // Mientras haya trabajos en curso, se consulta cada 5 s (el cron avanza la cola cada minuto).
    refetchInterval: activo ? 5000 : false,
  });
}

export function useEstadisticasIa() {
  return useQuery({
    queryKey: ['ia', 'stats'],
    queryFn: () => unwrap<EstadisticasIa>(api.GET('/api/v1/admin/ai/stats')),
  });
}

export function useProveedores() {
  return useQuery({
    queryKey: ['ia', 'proveedores'],
    queryFn: () => unwrap<Proveedor[]>(api.GET('/api/v1/admin/ai/providers')),
  });
}

export function useTareasIa() {
  return useQuery({
    queryKey: ['ia', 'tareas'],
    queryFn: () => unwrap<TareaIa[]>(api.GET('/api/v1/admin/ai/tasks')),
  });
}

export const ESTADOS_CASO: Record<EstadoCaso, string> = {
  review: 'En revisión',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  archived: 'Archivado',
};

export const PROVEEDORES_IA: Record<string, string> = {
  anthropic: 'Anthropic (Claude)',
  gemini: 'Google Gemini',
  openai: 'OpenAI',
  openai_compatible: 'Compatible con OpenAI',
};
